import { sleep } from '../shared/sleep'
import type {
  ClientRequest,
  ErrorCode,
  IngestClient,
  LoomductError,
  LoomductEvent,
} from './types'

interface ClientOptions {
  token: string
  host: string
  reportError: (error: LoomductError) => void
  log: (...args: unknown[]) => void
}

export interface Client extends IngestClient {
  getVisitorId: () => string | undefined
  reset: () => Promise<void>
  close: () => void
}

export function isVisitorId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length === 26 &&
    value.split('').every((char) => '0123456789abcdef'.includes(char))
  )
}

export function createClient(options: ClientOptions): Client {
  let visitorId: string | undefined
  let resets = 0
  let resetting: Promise<void> | undefined
  let stopped = false
  let tokens = 100
  let lastTake = Date.now()

  const report = (code: ErrorCode, message: string, init: ClientRequest) => {
    options.reportError({
      code,
      message,
      ...(init.kind === 'event' && { event: init.body as LoomductEvent }),
    })
  }

  const send = async (
    init: ClientRequest,
    attempt: number,
  ): Promise<unknown> => {
    if (stopped) {
      return undefined
    }
    const epoch = resets
    const url =
      options.host +
      init.path +
      (init.method === 'DELETE' ? '?_method=DELETE' : '')
    let body: string
    try {
      body = JSON.stringify({
        ...init.body,
        token: options.token,
        ...(visitorId && { visitorId }),
      })
    } catch (error) {
      report('invalid_event', String(error), init)
      return undefined
    }
    if (init.beacon && navigator.sendBeacon(url, body)) {
      return undefined
    }
    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        body,
        keepalive: !attempt,
      })
    } catch (error) {
      return retry(init, attempt, 'network', String(error))
    }
    const { status } = response
    options.log(init.path, status)
    if (response.ok) {
      const data = (await response.json().catch(() => undefined)) as
        | { visitorId?: unknown }
        | undefined
      const id = data?.visitorId
      if (isVisitorId(id) && epoch === resets) {
        if (init.method === 'DELETE') {
          visitorId = id
        } else {
          visitorId ??= id
        }
      }
      return data
    }
    if (status === 401) {
      stopped = true
      console.error('[loomduct] invalid token')
    }
    if (status === 429 || status >= 500) {
      return retry(init, attempt, 'server', `HTTP ${status}`)
    }
    report(
      status === 401
        ? 'invalid_token'
        : status === 413
          ? 'too_large'
          : 'invalid_event',
      `HTTP ${status}`,
      init,
    )
    return undefined
  }

  const retry = (
    init: ClientRequest,
    attempt: number,
    code: ErrorCode,
    message: string,
  ): Promise<unknown> | undefined => {
    if (stopped || init.beacon || init.retry === false || attempt > 1) {
      report(code, message, init)
      return undefined
    }
    return sleep((attempt ? 3000 : 1000) * (0.5 + Math.random())).then(() =>
      send(init, attempt + 1),
    )
  }

  return {
    request: async <T>(init: ClientRequest): Promise<T | undefined> => {
      if (resetting) {
        await Promise.race([resetting, sleep(2000)])
      }
      if (init.kind === 'event') {
        const now = Date.now()
        tokens = Math.min(100, tokens + (now - lastTake) / 100)
        lastTake = now
        if (tokens < 1) {
          report('rate_limited', 'rate limit', init)
          return undefined
        }
        tokens -= 1
      }
      return (await send(init, 0)) as T | undefined
    },
    getVisitorId: () => visitorId,
    reset: async () => {
      resets += 1
      visitorId = undefined
      const pending = send(
        { path: '/l/u', method: 'DELETE', body: {}, retry: false },
        0,
      ).then(() => {})
      resetting = pending
      await pending
      if (resetting === pending) {
        resetting = undefined
      }
    },
    close: () => {
      stopped = true
    },
  }
}
